'use client'
import {
  __async
} from "./chunk-BTG5EALA.mjs";

// src/OtpInputs/OtpInputs.tsx
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { jsx, jsxs } from "react/jsx-runtime";
var OtpInputs = ({
  numOfInput = 4,
  autoFocus = true,
  legend,
  onlyNumbers,
  onChange,
  onSubmit,
  autoSubmit = true,
  disabled,
  pulseActive = true,
  renderButton,
  error,
  formId,
  hideErrorSpace = true,
  shape = "default",
  className,
  defaultValue
}) => {
  const numberAsArray = "0".repeat(numOfInput).split("");
  const inputs = useRef([]);
  const [otp, setOtp] = useState(
    defaultValue ? defaultValue.split("") : numberAsArray.map(() => "")
  );
  useEffect(() => {
    if (onChange) {
      onChange(otp.join(""));
    }
  }, [otp]);
  useEffect(() => {
    if (autoFocus && inputs.current.length > 0) {
      focusInputByIndex(0);
    }
  }, [autoFocus]);
  const handleState = (idx, value) => {
    setOtp((prev) => prev.map((v, i) => i === idx ? value : v));
  };
  const handleResetState = () => {
    setOtp(numberAsArray.map(() => ""));
  };
  const isValidInput = (value) => {
    const regex = onlyNumbers ? /^[0-9]$/ : /^[a-z0-9]$/i;
    return regex.test(value) || value === "";
  };
  const focusInputByIndex = (index) => {
    const input = inputs.current[index];
    if (input) {
      setTimeout(() => {
        input.focus();
      }, 0);
    }
  };
  const handleForward = (idx) => {
    const canGoForward = numOfInput > idx;
    if (canGoForward) {
      focusInputByIndex(idx + 1);
    }
  };
  const handleBackwards = (idx) => {
    handleState(idx, "");
    const canGoBackward = idx < numOfInput;
    if (canGoBackward) {
      focusInputByIndex(idx - 1);
    }
  };
  const getCurrentIndex = (input) => {
    return Number(input.getAttribute("data-index"));
  };
  const handleKeyUp = (event) => {
    const inputValue = event.target.value;
    const desktopDelete = ["Backspace", "Delete"].includes(event.key);
    const isMobileInput = event.key === "Unidentified";
    const shouldDelete = (desktopDelete || isMobileInput) && !inputValue;
    if (shouldDelete) {
      const currendIdx = getCurrentIndex(event.currentTarget);
      handleBackwards(currendIdx);
    }
  };
  const handleChange = (event) => {
    var _a, _b, _c;
    const currendIdx = getCurrentIndex(event.currentTarget);
    let value = ((_a = event.nativeEvent) == null ? void 0 : _a.data) || "";
    if (!value) {
      value = ((_c = (_b = event.target) == null ? void 0 : _b.value) == null ? void 0 : _c[0]) || "";
    }
    const isValid = isValidInput(value);
    if (isValid) {
      handleState(currendIdx, value);
      const isLastInput = currendIdx === numOfInput - 1;
      if (autoSubmit && isLastInput && onSubmit && value) {
        const currentState = [...otp];
        currentState[currendIdx] = value;
        handleSubmit(currentState.join(""));
        handleBeatAnimation(event.target);
        return;
      }
      if (!value) {
        handleBackwards(currendIdx);
      } else {
        handleForward(currendIdx);
      }
    }
  };
  const handlePaste = (event) => {
    const data = event.clipboardData.getData("text/plain");
    const trimmed = data.trim();
    if (!trimmed.length) {
      return;
    }
    let index = getCurrentIndex(event.currentTarget);
    const lastIdx = numOfInput - 1;
    let localFullVal = "";
    for (const val of trimmed.split("")) {
      if (index > lastIdx)
        break;
      if (isValidInput(val)) {
        handleState(index, val);
        localFullVal += val;
        index++;
      }
    }
    const possibleLast = Math.min(index, lastIdx);
    const isLast = possibleLast === lastIdx;
    setTimeout(() => {
      focusInputByIndex(Math.min(index, lastIdx));
    }, 0);
    if (isLast && localFullVal.length === numOfInput) {
      handleSubmit(localFullVal);
      handleBeatAnimation(event.target);
    }
  };
  const handleBeatAnimation = (el, add = false) => {
    if (!pulseActive)
      return;
    if (add) {
      el.classList.add("animate-heart-beat");
    } else {
      el.classList.remove("animate-heart-beat");
    }
  };
  const handleSubmit = (otp2) => __async(void 0, null, function* () {
    try {
      if (onSubmit) {
        yield onSubmit(otp2);
      }
    } catch (error2) {
      focusInputByIndex(numOfInput - 1);
    }
  });
  return /* @__PURE__ */ jsxs(
    "form",
    {
      onSubmit: (e) => {
        e.preventDefault();
        handleSubmit(otp.join(""));
      },
      id: formId,
      name: formId,
      children: [
        /* @__PURE__ */ jsxs("fieldset", { className: "flex items-center justify-center gap-4 rtl:flex-row-reverse", children: [
          legend && /* @__PURE__ */ jsx("legend", { className: "w-full", children: legend }),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: twMerge(
                "flex items-center justify-center gap-4 rtl:flex-row-reverse",
                shape === "contained" && `bg-input hover:bg-input-hover has-[:focus]:bg-input-hover has-[:disabled]:bg-input-disabled has-[:disabled]:text-text/60 rounded-S has-[:focus]:border-input-border-focus placeholder:text-text-secondary h-11 w-full gap-2 border p-2 has-[:disabled]:cursor-not-allowed has-[:focus]:outline-none has-[:focus]:ring-0 ${error ? "border-error" : "border-input-border"}`,
                className
              ),
              children: numberAsArray.map((_, idx) => /* @__PURE__ */ jsx(
                "input",
                {
                  "data-index": idx.toString(),
                  "data-testid": `auth_otp_code_input_${idx}`,
                  ref: (r) => {
                    if (r)
                      inputs.current[idx] = r;
                  },
                  type: onlyNumbers ? "tel" : "text",
                  value: otp[idx] || "",
                  onChange: handleChange,
                  onKeyUp: handleKeyUp,
                  onKeyDown: (e) => {
                    e.target.select();
                    if (e.repeat) {
                      e.preventDefault();
                    }
                  },
                  onFocus: (e) => {
                    e.target.select();
                    handleBeatAnimation(e.target, true);
                  },
                  onBlur: (e) => {
                    handleBeatAnimation(e.target);
                  },
                  onPaste: handlePaste,
                  disabled,
                  className: twMerge(
                    shape === "default" && "border-input-border bg-input hover:bg-input-hover focus:border-input-border-focus focus:bg-input-hover disabled:bg-input-disabled disabled:text-text/60 disabled:border-input-border rounded-S h-12 w-full max-w-[60px] border text-center outline-none focus:ring-0 disabled:cursor-not-allowed",
                    shape === "contained" && `border-input-border focus:border-primary h-full w-full max-w-[20px] border-0 bg-transparent p-0 text-center outline-none focus:ring-0 ${otp[idx] ? "border-b-0 text-lg" : "border-b-2"}`
                  )
                },
                idx
              ))
            }
          )
        ] }),
        typeof error === "string" && !!error || !hideErrorSpace ? /* @__PURE__ */ jsx(
          "p",
          {
            className: `text-error my-2 min-h-[16px] text-center text-xs ${error ? "visible" : "text-transparent"}`,
            children: error
          }
        ) : null,
        !!renderButton && renderButton({ resetState: handleResetState })
      ]
    }
  );
};
var OtpInputs_default = OtpInputs;

export {
  OtpInputs_default
};
//# sourceMappingURL=chunk-SDJQ2QXJ.mjs.map